import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';

import SalesOrderInvoice from 'components/Invoices/SalesOrderInvoice';

import { updateSalesOrderStatus, updateSalesOrderPayment, updateSalesOrderPaymentDate } from 'redux/actions';
import { Delivered, Completed } from 'constants/status';
import { VAT_UAE_RECIPROCAL } from 'constants/globalVAT';
import { cash, card } from 'constants/paymentMethods';

const SOPaymentModal = (props) => {

  const printRef = useRef();

  const {displayId, customerId, soProducts, status, payments, total} = props.newSalesOrder;
  
  const [switchy, setSwitchy] = useState(0);
  const [currAmount, setCurrAmount] = useState("");
  const [invoiceDisplayId, setInvoiceDisplayId] = useState("");

  const paidAmount = payments.reduce((prev, curr) => prev + curr.amount, 0);
  const pendingAmount = total - paidAmount;
  const currAmountWithoutVAT = Math.round( currAmount / VAT_UAE_RECIPROCAL );
  const amountVAT = currAmount - currAmountWithoutVAT;

  const columns = [
    { field: 'displayId', headerName: 'Invoice ID', flex: 1, sortable: false, disableColumnMenu: true },
    { field: 'date', headerName: 'Payment Date', flex: 1, sortable: false, disableColumnMenu: true },
    { field: 'amount', headerName: 'Amount', flex: 1, sortable: false, disableColumnMenu: true },
    { field: 'paymentMethod', headerName: 'Payment Method', flex: 1, sortable: false, disableColumnMenu: true }
  ];
  
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Sales Order Invoice"
  });

  const createInvoiceDisplayId = () => {
    const invoiceNum = payments.length + 1;
    return (displayId + `/INV/${invoiceNum}`);
  };

  const handlePayment = (paymentMethod) => {
    const invoiceDisplayId = createInvoiceDisplayId();
    setInvoiceDisplayId(invoiceDisplayId);
    
    //This is a temporary method to get the salesOrder id. improve it. also salesOrderTable statusButton already
    //gets the salesOrderInfo using the find method, so here we are doing it twice.
    const salesOrderInfo = props.salesOrders.find(salesOrder => salesOrder.displayId === displayId && salesOrder.total === total);
    if(salesOrderInfo) {

      props.updateSalesOrderPayment({
        id: salesOrderInfo.id,
        payment: {
          displayId: invoiceDisplayId,
          amountVat: amountVAT,
          amount: currAmount,
          paymentMethod
        }
      }, props.user.adminId);

      if( pendingAmount - currAmount <= 0 ) {
        props.updateSalesOrderPaymentDate({ id: salesOrderInfo.id }, props.user.adminId);
        
        const everyStatusDelivered = soProducts.every(product => product.status === Delivered);
        if(everyStatusDelivered) props.updateSalesOrderStatus({ id: salesOrderInfo.id, status: Completed }, props.user.adminId);
      }
      setSwitchy(1);
    }
    else props.closeModal();
  };

  const createInvoiceInfo = () => {
  const customerInfo = props.customers.find(customer => customer.id === customerId);
    return {
      displayId: invoiceDisplayId || (`${displayId}/DRAFT`),
      customerName: customerInfo.name,
      customerPhone: customerInfo.phone,
      paymentDate: new Date(), //maybe different for refund modal
      subTotal: currAmountWithoutVAT,
      taxTotal: amountVAT,
      currentAmount: currAmount,
      grandTotal: total,
      products: soProducts,
      payments,
      status
    }
  };

  const renderAll = () => {
    switch (switchy) {
      case 0:
        return (
          <Box>
            <Box
              sx={{
                overflow: 'auto',
                height: 266,
                marginBottom: '1%'
              }}
            >
              <DataGrid
                rows={payments.map(payment => ({
                  displayId: payment.displayId,
                  date: moment(payment.createdAt).format('DD/MM/YYYY'),
                  amount: payment.amount,
                  paymentMethod: payment.paymentMethod
                }))}
                columns={columns}
                getRowId={(row) => row.displayId}
                disableSelectionOnClick
                hideFooter
                localeText={{
                  noRowsLabel: 'No Payments'
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                color="textPrimary"
                variant="h6"
                gutterBottom
              >
                {`Pending Amount : AED ${Number(pendingAmount).toFixed(2)}`}
              </Typography>
              <TextField
                helperText="Please Enter the Current Invoice Amount"
                label="Invoice Amount"
                name="currAmount"
                onChange={event => setCurrAmount( parseFloat(event.target.value) || 0 )}
                value={currAmount}
                variant="outlined"
              />
              <Typography
                color="textPrimary"
                variant="h6"
                gutterBottom
              >
                {//This Displays the Current Invoice Amount with the VAT included.
                `Invoice Amount Breakdown : AED ${currAmountWithoutVAT} + ${amountVAT} ( VAT )`}
              </Typography>
              <Typography
                color="textPrimary"
                variant="h6"
                gutterBottom
              >
                {//This Displays the Balance Amount that will be left after current payment with VAT included.
                `Balance Amount : AED ${Math.round( pendingAmount - currAmount < 0 ? 0 : pendingAmount - currAmount )}`}
              </Typography>
              { currAmount <= 0 ?
                <Button
                  variant="contained"
                  color="primary"
                  onClick={setSwitchy.bind(this, 1)}
                >
                  Print Invoice
                </Button> :
                <Box sx={{ display: "flex" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePayment.bind(this, cash)}
                  >
                    Cash Payment
                  </Button>
                  <Divider
                    style={{ margin: "10px" }}
                    orientation="vertical"
                    flexItem
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePayment.bind(this, card)}
                  >
                    Card Payment
                  </Button>
                </Box>
              }
            </Box>
          </Box>
        )

      case 1:
        return (
          <Box>
            <Paper
              style={{
                overflow: 'auto',
                maxHeight: 462
              }}
            >
              <SalesOrderInvoice
                ref={printRef}
                info={createInvoiceInfo()}
                user={props.user}
              />
            </Paper>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '1%'
              }}
            >
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  handlePrint();
                  props.closeModal();
                }}
              >
                Print Invoice
              </Button>
            </Box>
          </Box>
        )

      default:
        return (<React.Fragment></React.Fragment>)
    }
  };

  return (
    <Modal open={props.open}>
      <Card>
        <CardHeader
          subheader="Please Review Previous Payments."
          title="Make Payment"
        />
        <Divider />
        <CardContent>
          {renderAll()}
        </CardContent>
      </Card>
    </Modal>
  )
};

export default connect (null, {updateSalesOrderStatus, updateSalesOrderPayment, updateSalesOrderPaymentDate}) (SOPaymentModal);